<template>
  <div>
    <label :for="$props.id" class="block text-sm/6 font-medium text-gray-900" v-show="label">{{ label }}</label>
    <div class="mt-2 grid grid-cols-1 relative">
      <input
        :id="$props.id"
        :name="$props.id"
        :type="inputType"
        :placeholder="$props.placeholder"
        v-bind="$attrs"
        :maxlength="maxlength"
        v-model="inputValue"
        class="block w-full rounded-md px-3 py-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
        :class="[
          customClass,
          {
            'col-start-1 row-start-1 pl-3 pr-10 text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600 sm:pr-9':
              errorMessage !== undefined
          }
        ]"
        aria-invalid="true"
        aria-describedby="email-error"
        @blur="blur"
        @click="checkCapsLockStatus"
        @keydown="checkCapsLockStatus"
        @input="update($event.target.value)"
      />
      <!-- Loading spinner -->
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
        v-show="showLoading && !errorMessage"
      >
        <div class="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
      <ExclamationCircleIcon
        v-show="errorMessage"
        class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4"
        aria-hidden="true"
      />
    </div>

    <!-- Error Message  dynamic  -->
    <div
      v-if="!errorWrapper && errorMessage"
      class="mt-1 text-xs text-red-600 h-4 md:h-5 overflow-hidden"
      :class="{ 'opacity-0': !errorMessage, 'opacity-100': errorMessage, 'transition-opacity duration-200': true }"
    >
      {{ errorMessage }}
    </div>
    <!-- Error Message with Fixed Height -->
    <div
      v-if="errorWrapper"
      class="mt-1 text-xs text-red-600 h-4 md:h-5 overflow-hidden"
      :class="{ 'opacity-0': !errorMessage, 'opacity-100': errorMessage, 'transition-opacity duration-200': true }"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { ExclamationCircleIcon } from '@heroicons/vue/16/solid';
import { useField } from 'vee-validate';
import moment from 'moment';
import { ref, onBeforeMount, watch } from 'vue';
import { generateYupSchema } from '@/composables/globalHelperFunctions';
import { generateYupSchemaForBsn } from '@/components/shared/validationRules/bsnValidationRules';
import { generateYupSchemaForEmail } from '@/components/shared/validationRules/emailValidationRules';
import { generateYupSchemaForDate } from '@/components/shared/validationRules/dateValidationRules';
import { Number } from 'core-js';

export default {
  name: 'InputGroups',
  inheritedAttrs: true,
  components: {
    ExclamationCircleIcon
  },
  props: {
    id: {
      default: '',
      type: String
    },
    placeholder: {
      default: '',
      type: String
    },
    type: {
      defualt: 'text',
      type: String
    },
    label: {
      default: '',
      type: String
    },
    modelValue: { type: [String, Number] },
    maxlength: {
      type: [String, Number]
    },
    rules: {
      default: () => ({}),
      type: Object
    },
    showCapsLockStatus: {
      default: false,
      type: Boolean
    },
    isBsn: {
      default: false,
      type: Boolean
    },
    isEmail: {
      default: false,
      type: Boolean
    },
    isDate: {
      default: false,
      type: Boolean
    },
    showLoading: {
      default: false,
      type: Boolean
    },
    customClass: {
      default: '',
      type: String
    },
    useIdForErrorMessage: {
      default: false,
      type: Boolean
    },
    errorWrapper: {
      default: true,
      type: Boolean
    }
  },
  emits: ['inputValue', 'isValid'],
  setup(props, { emit }) {
    const inputValue = ref(props.modelValue);
    const schema = ref(null);
    if (props.rules && props.rules.isBsn) {
      schema.value = generateYupSchemaForBsn(props);
    } else if (props.rules && props.rules.isEmail) {
      schema.value = generateYupSchemaForEmail(props);
    } else if (props.rules && props.rules.isDate) {
      schema.value = generateYupSchemaForDate(props);
    } else {
      schema.value = generateYupSchema(props);
    }

    const { value, errorMessage, validate } = useField(props.id, schema);

    watch(
      () => props.rules,
      () => {
        if (props.rules && props.rules.isBsn) {
          schema.value = generateYupSchemaForBsn(props);
        } else if (props.rules && props.rules.isEmail) {
          schema.value = generateYupSchemaForEmail(props);
        } else if (props.rules && props.rules.isDate) {
          schema.value = generateYupSchemaForDate(props);
        } else {
          schema.value = generateYupSchema(props);
        }
      }
    );
    const inputType = ref(props.type);
    const isCapsLockOnMessageVisible = ref(false);

    const blur = () => {
      isCapsLockOnMessageVisible.value = false;
      validate();
    };

    const checkCapsLockStatus = (event) => {
      isCapsLockOnMessageVisible.value =
        event.target.value.toUpperCase() === event.target.value &&
        event.target.value.toLowerCase() !== event.target.value;
    };

    const update = async (newValue) => {
      if (props.rules.isDate) {
        newValue = newValue.replace(/[^\0-9]/gi, '');
        if (
          newValue.length === 8 &&
          newValue.indexOf('-') === -1 &&
          newValue.indexOf('/') === -1 &&
          newValue.indexOf('.') === -1
        ) {
          newValue = newValue.replace(/^(\d{2})(\d{2})/, '$1-$2-');
        }
        // If date is 12.12.2012 format
        if (newValue.indexOf('.') > -1) {
          newValue = newValue.replace(/[.]/g, '-');
        }
        inputValue.value = newValue;
        let validDate = newValue ? (newValue.length == 10 ? new moment(newValue, 'DD-MM-YYYY') : newValue) : '';
        value.value = newValue ? (newValue.length == 10 ? validDate.format('DD-MM-YYYY') : newValue) : '';
        emit('inputValue', newValue.length == 10 ? validDate.format('DD-MM-YYYY') : value.value);
      } else {
        emit('isValid', false);
        inputValue.value = newValue;
        value.value = newValue;
      }
      let result = await validate();
      emit('isValid', result.valid);
    };

    onBeforeMount(() => {
      inputType.value = props.type;
      if (props.modelValue) {
        value.value = props.modelValue;
      }
    });

    return {
      inputValue,
      inputType,
      isCapsLockOnMessageVisible,
      blur,
      checkCapsLockStatus,
      update,
      errorMessage,
      validate
    };
  }
};
</script>
